import { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider } from '@kidshome/ui';
import i18n from '@/app/i18n';
import { useAuthStore } from '@/entities/session';
import { ToastProvider } from '@/shared/ui';
import {
  installOutbox,
  outboxController,
  UpdatePrompt,
  OfflineStatus,
  clearApiCache,
} from '@/shared/pwa';

/**
 * App-level providers (FSD `app` layer): TanStack Query, react-i18next, and the UI ThemeProvider
 * (which reflects the persisted theme/mode onto `<html>`; first paint is handled pre-bundle by the
 * inline script in index.html). RouterProvider is mounted by the entry inside these.
 *
 * The offline outbox (ADR-0018) is wired to THIS QueryClient on mount so replayed mutations can
 * invalidate the right queries, and to the auth store so every queued write is stamped with (and only
 * ever replayed under) its enqueuing member — the device is shared, so kid A's offline work must
 * never be sent under kid B's session. The SW update toast + offline/sync status are mounted globally
 * so they overlay every screen. (The install affordance is part of the per-persona shell header.)
 */
export function AppProviders({ children }: { children: React.ReactNode }): React.ReactElement {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
            staleTime: 30_000,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  useEffect(() => {
    const teardown = installOutbox(
      queryClient,
      () => useAuthStore.getState().user?.id ?? null,
      () => useAuthStore.getState().actingAs?.householdId ?? null,
    );
    // On login/logout/member-switch: re-scope the pending badge and drain the returning member's
    // queue (their entries waited while someone else — or no one — was signed in).
    let prevOwner = useAuthStore.getState().user?.id ?? null;
    // On entering/exiting/switching platform-owner impersonation (ADR-0055): a write enqueued under one
    // acting-as household only replays while the owner is LIVE-acting in that SAME household
    // (`outbox-controller.ts`'s scope gate). Without a trigger here, an entry queued before the owner
    // exits (or switches households) sits skipped until an UNRELATED trigger (online/focus/the 20s poll)
    // happens to fire after the owner re-enters the matching scope — replay it eagerly on the transition
    // itself, exactly like the owner-switch case above.
    let prevActingAs = useAuthStore.getState().actingAs?.householdId ?? null;
    const unsubscribe = useAuthStore.subscribe((state) => {
      const nextOwner = state.user?.id ?? null;
      if (nextOwner !== prevOwner) {
        const wasSignedIn = prevOwner !== null;
        prevOwner = nextOwner;
        outboxController.ownerChanged();
        // Single teardown seam for EVERY logout cause (per-shell button AND a forced 401 -> onAuthFailure
        // expiry that only clears the store): when the owner goes non-null -> null on this shared device,
        // purge the previous account's data so the next member can't see their balances/PII/ledger.
        if (wasSignedIn && nextOwner === null) {
          // TanStack cache (balances, members, …).
          queryClient.clear();
          // Service-worker runtime API cache (keyed by URL only) so cached reads aren't replayed offline.
          clearApiCache();
        }
      }
      const nextActingAs = state.actingAs?.householdId ?? null;
      if (nextActingAs !== prevActingAs) {
        prevActingAs = nextActingAs;
        outboxController.scheduleReplay();
      }
    });
    return () => {
      unsubscribe();
      teardown();
    };
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <ThemeProvider>
          <ToastProvider>
            {children}
            <UpdatePrompt />
            <OfflineStatus />
          </ToastProvider>
        </ThemeProvider>
      </I18nextProvider>
    </QueryClientProvider>
  );
}
